import { Component } from '@angular/core';

import { select, Store } from '@ngrx/store';
import jwt_decode from 'jwt-decode';
import * as moment from 'moment';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
  filter,
  skipWhile,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { Subscription, timer } from 'rxjs';

import { AppState, selectAccountState } from './core/reducers';
import {
  getAccount,
  logout,
  logoutSuccessful,
} from './core/store/account/account.actions';
import { isAuth } from 'src/app/core';
import { JwtService, TranslationService } from './core/services';
import { Actions, ofType } from '@ngrx/effects';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less'],
})
export class AppComponent {
  timerSubsciption: Subscription;

  constructor(
    private translate: TranslationService,
    private store: Store<AppState>,
    private jwtService: JwtService,
    private modal: NzModalService,
    private actions$: Actions
  ) {
    this.translate.initLocale();
  }

  ngOnInit() {
    this.timerUnsub();
    this.checkAuth();
    this.store.dispatch(getAccount());
  }

  timerUnsub() {
    this.actions$
      .pipe(ofType(logoutSuccessful), untilDestroyed(this))
      .subscribe(() => {
        this.timerSubsciption?.unsubscribe();
      });
  }

  dispatchLogout() {
    this.store.dispatch(logout());
  }

  checkAuth() {
    this.store
      .pipe(
        select(selectAccountState),
        skipWhile((state) => state.isLoading),
        take(1),
        switchMap(() =>
          this.store.pipe(
            select(isAuth),
            tap((authed) => {
              if (authed) this.checkTokenExpiration();
            })
          )
        )
      )
      .subscribe();
  }

  checkTokenExpiration(): void {
    const token = this.jwtService.getToken();

    if (!token) this.dispatchLogout();

    const msInterval = 1000;
    const tokenExpDate = moment(jwt_decode(token)['exp'] * 1000);
    const secondsToExpired = tokenExpDate.diff(moment(), 'seconds');

    this.timerSubsciption = timer(msInterval, msInterval)
      .pipe(
        takeWhile((count) => {
          if (secondsToExpired <= 0) {
            return true;
          }

          return count <= secondsToExpired;
        }),
        filter((count) => count >= secondsToExpired),
        tap(() => this.showSessionTimeoutModal()),
        take(1)
      )
      .subscribe();
  }

  showSessionTimeoutModal(): void {
    this.translate
      .stream([
        'PAGE.GLOBAL.AUTH_TOKEN.SESSION_EXPIRED_MODAL.TITLE',
        'PAGE.GLOBAL.AUTH_TOKEN.SESSION_EXPIRED_MODAL.CONTENT',
      ])
      .pipe(take(1))
      .subscribe((translation) => {
        this.modal.warning({
          nzTitle:
            translation['PAGE.GLOBAL.AUTH_TOKEN.SESSION_EXPIRED_MODAL.TITLE'],
          nzContent:
            translation['PAGE.GLOBAL.AUTH_TOKEN.SESSION_EXPIRED_MODAL.CONTENT'],
          nzOnOk: () => {
            this.dispatchLogout();
          },
        });
      });
  }
}
